import { createSlice } from '@reduxjs/toolkit';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { jsx, jsxs } from 'react/jsx-runtime';

// Shared across all Centilio products. Errors and entitlement blocks are STATE,
// not routes — rendered as an overlay by SystemStateBoundary.
const systemStatusSlice = createSlice({
  name: 'systemStatus',
  initialState: {
    phase: 'boot',
    block: null
  },
  reducers: {
    setReady(state) {
      state.phase = 'ready';
      state.block = null;
    },
    setBlock(state, action) {
      const {
        kind,
        ctx
      } = action.payload || {};
      state.block = {
        kind,
        ctx: ctx || {}
      };
    },
    clearBlock(state) {
      state.block = null;
    }
  }
});
const {
  setReady,
  setBlock,
  clearBlock
} = systemStatusSlice.actions;
var systemStatusSlice_default = systemStatusSlice.reducer;

// Config-driven registry: ONE entry per blocking kind, shared by every product.
// Product-specific copy/links come from `config`, so the screens are identical
// across SEO Bot / Accounts / Sign / Vault / Drive while the product name and
// deep links differ.
//
// config = {
//   productName,                         // e.g. "Centilio SEO Bot"
//   accountsBaseUrl,                     // e.g. "https://account.centilio.com"
//   accountsApplicationsLink(orgId)?,    // override for the directory deep link
//   loginUrl?, accountsHome?,
// }

const stripSlash = s => (s || '').replace(/\/+$/, '');
function links(config) {
  const base = stripSlash(config.accountsBaseUrl || '');
  return {
    apps: orgId => config.accountsApplicationsLink ? config.accountsApplicationsLink(orgId) : `${base}/#/c${orgId}/directory/applications`,
    login: config.loginUrl || `${base}/#/login`,
    home: config.accountsHome || base
  };
}
function resolveStatus(kind, ctx = {}, config = {}) {
  const P = config.productName || 'this product';
  const L = links(config);
  const orgId = ctx.orgId;
  const isOrgAdmin = !!ctx.isOrgAdmin;
  const REFRESH = {
    label: 'Refresh',
    type: 'reload'
  };
  const RETRY = {
    label: 'Retry',
    type: 'reload'
  };
  switch (kind) {
    case 'not_activated':
      if (isOrgAdmin) {
        return {
          variant: 'fullscreen',
          title: `${P} isn't activated for your organisation`,
          description: `${P} hasn't been activated for your organisation yet. As an administrator, you can enable it now from the Accounts directory.`,
          actions: [{
            label: 'Activate in Accounts',
            type: 'link',
            href: L.apps(orgId)
          }, REFRESH]
        };
      }
      return {
        variant: 'fullscreen',
        title: `${P} isn't available yet`,
        description: `${P} hasn't been activated for your organisation yet. You can ask an administrator to enable it.`,
        actions: [{
          label: 'Request to enable',
          type: 'request'
        }, REFRESH]
      };
    case 'not_assigned':
      if (isOrgAdmin) {
        return {
          variant: 'fullscreen',
          title: `You're not assigned to ${P}`,
          description: `${P} is enabled for your organisation but isn't assigned to you yet. Open Accounts to assign users (including yourself).`,
          actions: [{
            label: 'Manage access in Accounts',
            type: 'link',
            href: L.apps(orgId)
          }, REFRESH]
        };
      }
      return {
        variant: 'fullscreen',
        title: `You don't have access to ${P}`,
        description: `Your organisation hasn't given you access to ${P} yet. Send a request and your administrators will be notified to approve it.`,
        actions: [{
          label: 'Request access',
          type: 'request'
        }, REFRESH]
      };
    case 'request_sent':
      return {
        variant: 'fullscreen',
        title: 'Request sent',
        description: `Your request to access ${P} has been sent to your organisation's administrators. We'll email you as soon as it's approved.`,
        subDescription: ctx.requestedTime ? `Requested on ${new Date(Number(ctx.requestedTime)).toLocaleString()}.` : null,
        actions: [REFRESH]
      };
    case 'unauthenticated':
      return {
        variant: 'fullscreen',
        title: 'Your session has expired',
        description: `Please sign in again to continue to ${P}.`,
        actions: [{
          label: 'Go to sign in',
          type: 'link',
          href: L.login
        }]
      };
    case 'org_not_found':
      return {
        variant: 'fullscreen',
        title: 'Organisation not found',
        description: "We couldn't find that organisation, or you don't have access to it. Pick an organisation from your Accounts portal.",
        actions: [{
          label: 'Go to Accounts',
          type: 'link',
          href: L.home
        }]
      };
    case 'forbidden':
      return {
        variant: 'fullscreen',
        title: "You don't have access to this section",
        description: ctx.message || 'Your role does not permit access here. Contact an organisation admin if you believe this is a mistake.',
        actions: [{
          label: 'Refresh',
          type: 'reload'
        }]
      };
    case 'server_error':
      return {
        variant: ctx.variant || 'fullscreen',
        code: '500',
        title: "We couldn't load your workspace",
        description: ctx.message || 'Something went wrong on our end. Please try again in a few minutes, or contact your administrator if this keeps happening.',
        subDescription: ctx.traceId ? `Reference id: ${ctx.traceId}` : null,
        actions: [RETRY]
      };
    case 'network_error':
      return {
        variant: ctx.variant || 'fullscreen',
        title: 'Connection problem',
        description: `We couldn't reach ${P}. Check your internet connection and try again.`,
        actions: [RETRY]
      };
    case 'route_not_found':
      return {
        variant: 'fullscreen',
        code: '404',
        title: "Sorry, the page you're looking for cannot be found.",
        description: 'The page may have moved, or the link may be broken.',
        actions: [{
          label: 'Back to home',
          type: 'home'
        }]
      };
    default:
      return {
        variant: 'fullscreen',
        code: '500',
        title: 'Something went wrong',
        description: 'An unexpected error occurred. Please refresh and try again.',
        actions: [RETRY]
      };
  }
}

function defaultAction(action, config) {
  switch (action && action.type) {
    case 'link':
      if (action.href) window.open(action.href, '_self');
      break;
    case 'reload':
      window.location.reload();
      break;
    case 'home':
      if (config.onHome) config.onHome();else window.location.href = config.homeUrl || window.location.origin + '/';
      break;
  }
}

// Framework-agnostic (no MUI) inline-styled full-screen presenter. Works in CRA
// and Vite, React 18 and 19. Product passes `config` for product name, image,
// theme colour, and the onRequestAccess handler.
function SystemStatePage({
  descriptor,
  ctx = {},
  config = {}
}) {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!descriptor) return null;
  const primary = config.primaryColor || '#1a1d3f';
  const {
    code,
    title,
    description,
    subDescription,
    actions = []
  } = descriptor;
  const handle = action => {
    if (action && action.type === 'request') {
      if (!config.onRequestAccess) return;
      setBusy(true);
      setErr('');
      Promise.resolve(config.onRequestAccess(ctx.orgId)).then(() => dispatch(setBlock({
        kind: 'request_sent',
        ctx: {
          ...ctx,
          requestedTime: Date.now()
        }
      }))).catch(() => setErr("We couldn't send your request. Please try again.")).finally(() => setBusy(false));
      return;
    }
    defaultAction(action, config);
  };
  return /*#__PURE__*/jsx("div", {
    style: S.overlay,
    role: "alert",
    "aria-live": "assertive",
    children: /*#__PURE__*/jsxs("div", {
      style: S.panel,
      children: [config.imageUrl && /*#__PURE__*/jsx("img", {
        src: config.imageUrl,
        alt: "status",
        style: S.img
      }), /*#__PURE__*/jsxs("div", {
        style: S.content,
        children: [code && /*#__PURE__*/jsx("div", {
          style: {
            ...S.code,
            color: primary
          },
          children: code
        }), title && /*#__PURE__*/jsx("div", {
          style: {
            ...S.title,
            color: primary
          },
          children: title
        }), description && /*#__PURE__*/jsx("div", {
          style: S.text,
          children: description
        }), subDescription && /*#__PURE__*/jsx("div", {
          style: S.sub,
          children: subDescription
        }), err && /*#__PURE__*/jsx("div", {
          style: {
            ...S.text,
            color: '#b91c1c'
          },
          children: err
        }), /*#__PURE__*/jsx("div", {
          style: S.actions,
          children: actions.map((a, i) => {
            const isReq = a.type === 'request';
            const isPrimary = i === 0;
            return /*#__PURE__*/jsx("button", {
              disabled: busy && isReq,
              onClick: () => handle(a),
              style: {
                ...S.btn,
                background: isPrimary ? primary : 'transparent',
                color: isPrimary ? '#fff' : primary,
                border: isPrimary ? 'none' : '1px solid #d0d5dd'
              },
              children: busy && isReq ? 'Sending…' : a.label
            }, `${a.label}-${i}`);
          })
        })]
      })]
    })
  });
}
const S = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 4000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#ffffff',
    padding: 24,
    boxSizing: 'border-box',
    fontFamily: 'Inter, system-ui, sans-serif'
  },
  panel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    maxWidth: 920,
    width: '100%',
    flexWrap: 'wrap'
  },
  img: {
    width: 320,
    height: 280,
    maxWidth: '55vw',
    objectFit: 'contain'
  },
  content: {
    padding: '16px 24px',
    maxWidth: 480
  },
  code: {
    fontSize: 'calc(1.5rem + 4vw)',
    fontWeight: 600,
    lineHeight: 1.1
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '0.75rem'
  },
  text: {
    fontSize: '1rem',
    color: '#64748b',
    marginBottom: '1rem'
  },
  sub: {
    fontSize: '0.85rem',
    color: '#94a3b8',
    marginBottom: '1rem'
  },
  actions: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 8
  },
  btn: {
    padding: '10px 22px',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: '0.9375rem',
    fontFamily: 'inherit'
  }
};

function SystemStateBoundary({
  children,
  config = {}
}) {
  const block = useSelector(s => s.systemStatus && s.systemStatus.block);
  if (!block || !block.kind) return children;
  const descriptor = resolveStatus(block.kind, block.ctx, config);
  return /*#__PURE__*/jsx(SystemStatePage, {
    descriptor: descriptor,
    ctx: block.ctx,
    config: config
  });
}

// Pure mapping from an axios-style error to a systemStatus block. Shared by every
// product's init flow / interceptor so error classification is identical.
//   - no response            -> network_error
//   - 403 app_access_denied  -> not_assigned | not_activated | request_sent (PENDING)
//   - 401                    -> unauthenticated
//   - 400 org_not_found      -> org_not_found
//   - >=500                  -> server_error
//   - other 4xx              -> null (caller handles inline)
function mapErrorToBlock(error, fallbackOrgId) {
  if (!error || !error.response) return {
    kind: 'network_error',
    ctx: {}
  };
  const {
    status,
    data
  } = error.response;
  const det = data && data.error && data.error.details || {};
  const code = data && data.error && data.error.code || data && data.message || '';
  const codeStr = String(code);
  const orgId = det.org_id || data && data.default_organizations && data.default_organizations.Id || fallbackOrgId;
  if (status === 403 && codeStr.includes('app_access_denied')) {
    const reason = det.reason;
    const isOrgAdmin = !!det.is_org_admin;
    const ar = det.access_request || {};
    const ctx = {
      orgId,
      isOrgAdmin,
      reason,
      requestedTime: ar.requested_time
    };
    if (!isOrgAdmin && ar.status === 'PENDING') return {
      kind: 'request_sent',
      ctx
    };
    return {
      kind: reason === 'NOT_ASSIGNED' ? 'not_assigned' : 'not_activated',
      ctx
    };
  }
  if (status === 401) return {
    kind: 'unauthenticated',
    ctx: {}
  };
  if (status === 400 && codeStr.includes('org_not_found')) return {
    kind: 'org_not_found',
    ctx: {
      orgId
    }
  };
  if (status >= 500) return {
    kind: 'server_error',
    ctx: {
      orgId
    }
  };
  return null;
}

export { SystemStateBoundary, SystemStatePage, clearBlock, mapErrorToBlock, resolveStatus, setBlock, setReady, systemStatusSlice_default as systemStatusReducer };
